import { computed, makeAutoObservable, observable, action } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import addError from 'src/utils/error.jsx';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import confirmation from 'src/utils/confirm.jsx';
import { DateTime } from 'src_common/utils/time/time';
import { ApiMarkersOfHarm } from 'src/components/MarkersOfHarm/ApiMarkersOfHarm';
import {
    MarkersOfHarmMohInteractionStatusZod,
    MarkersOfHarmMohInteractionTypeZod,
    MarkersOfHarmMohRiskLevelZod,
} from 'src/api_openapi/v2/markersOfHarm/schemas';
import { Common } from 'src/AppState/Common';
import { ZodType } from 'zod';
import { ApiCreateMarkersOfHarmParamsType } from 'src/api_openapi/v2/markersOfHarm/apiCreateMarkersOfHarm';
import { ApiGetMarkersOfHarmListResponse200Type } from 'src/api_openapi/v2/markersOfHarm/apiGetMarkersOfHarmList';
import { searchStaffAccountList } from 'src_server/types/accountData';
import { Amount } from 'src_common/common/amount/Amount';

function isValidType<T>(schema: ZodType<T>, value: unknown): T | false {
    const parseResult = schema.safeParse(value);
    return parseResult.success ? parseResult.data : false;
}

const INTERACTIONS_TYPE = [
    { value: 'email', label: 'Email' },
    { value: 'phone-call', label: 'Phone call' },
    { value: 'pop-up', label: 'Pop-up' },
    { value: 'live-chat', label: 'Live chat' },
    { value: 'social-media', label: 'Social media' },
    { value: 'whats-app', label: 'WhatsApp' },
    { value: 'other', label: 'Other' },
];

const REASON_TYPE = [
    { value: 'account-indicators', label: 'Account Indicators' },
    { value: 'casino-session', label: 'Casino Session' },
    { value: 'customer-led-contact', label: 'Customer-led Contact' },
    { value: 'customer-spend', label: 'Customer Spend' },
    { value: 'deposit-patterns', label: 'Deposit Patterns' },
    { value: 'expired-se-return', label: 'Expired SE Return' },
    { value: 'failed-deposits', label: 'Failed Deposits' },
    { value: 'failed-srg-pop-up', label: 'Failed SRG Pop Up' },
    { value: 'free-bet-requests', label: 'Free Bet Requests' },
    { value: 'gambling-behaviour', label: 'Gambling Behaviour' },
    { value: 'high-velocity-casino', label: 'High Velocity Casino' },
    { value: 'high-velocity-spend', label: 'High Velocity Spend' },
    { value: 'high-velocity-sportsbook', label: 'High Velocity Sportsbook' },
    { value: 'increase-deposits', label: 'Increase Deposits' },
    { value: 'increase-spend', label: 'Increase Spend' },
    { value: 'patterns-of-spend', label: 'Patterns of Spend' },
    { value: 'proof-of-funds', label: 'Proof of Funds' },
    { value: 'rg-tools', label: 'RG Tools' },
    { value: 'time-spent-gambling', label: 'Time Spent Gambling' },
    { value: 'high-losses', label: 'High Losses' },
    { value: 'kyc-indicator', label: 'KYC Indicator' },
    { value: 'other', label: 'Other' },
];

const MOH_RISK_LEVEL = [
    { value: 'none', label: 'No risk' },
    { value: 'very-low', label: 'Very low' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'very-high', label: 'Very high' },
];

const INTERACTION_STATUS = [
    { value: 'successful', label: 'Successful' },
    { value: 'unsuccessful', label: 'Unsuccessful' },
];

export interface InteractionItemListType {
    mohId: number;
    accountId: string;
    universe: string;
    interactionType: string;
    interactionTimestamp: string;
    nextReviewTimestamp: string;
    note: string;
    reason: string;
    riskLevel: string;
    interactionStatus: string;
    profitLoss: string;
    agent: string;
}

export interface InteractionType {
    accountId: string;
    universe: string;
    interactionType: string;
    interactionTimestamp: string;
    nextReviewTimestamp?: string;
    note?: string;
    reason: string;
    riskLevel: string;
    interactionStatus: string;
    profitLoss: Amount | undefined;
    agent: string;
}

type InteractionsType = ApiGetMarkersOfHarmListResponse200Type['interactions'];
type InteractionsTypeReadOnlyType = (InteractionType & {
    mohId: number;
    profitLoss: Amount | undefined;
    labels: {
        interactionType: string;
        reason: string;
        riskLevel: string;
        interactionStatus: string;
    };
})[];
export type StaffListType = searchStaffAccountList.SuccessResponseType['results'];
export type InteractionsItemType = InteractionsType[number];
export class MarkersOfHarmState {
    @observable public editingIndex: number = -1;

    private apiMarkersOfHarm: ApiMarkersOfHarm;
    public readonly getInteractionsResource: Resource<InteractionsType>;
    public readonly getStaffListResource: Resource<StaffListType>;
    private readonly sortBy: 'latestMohId' | 'latest' | 'oldest' = 'latestMohId';

    public readonly selectedInteractionType: FormInputState<string, string>;
    public readonly nextReviewTimestamp: FormInputState<string, string>;
    public readonly note: FormInputState<string, string>;
    public readonly selectedReason: FormInputState<string, string>;
    public readonly selectedRiskLevel: FormInputState<string, string>;
    public readonly interactionStatus: FormInputState<string, string>;
    public readonly profitLoss: FormInputState<string, Amount | undefined>;

    public readonly form: FormModel<InteractionType>;

    public constructor(
        private readonly customerId: () => number,
        private readonly configState: ConfigState,
        private readonly common: Common
    ) {
        this.apiMarkersOfHarm = new ApiMarkersOfHarm(this.common);
        this.getInteractionsResource = new Resource(async (): Promise<InteractionsType> => {
            const response = await this.apiMarkersOfHarm.getListMarkersOfHarm({
                account_id: this.customerId().toString(),
            });

            if (response.status === 200) {
                return response.body.interactions;
            }

            return [];
        });

        this.getStaffListResource = new Resource(async (): Promise<StaffListType> => {
            const response = await this.common.trpcClient.client.accountData.searchStaffList.mutate();

            if (response.responseStatus === 'success') {
                return response.data.results;
            }

            return [];
        });
        this.selectedInteractionType = FormInputState.new('').map(validateNotEmpty('Please select Interaction type.'));
        this.nextReviewTimestamp = FormInputState.new('');
        this.note = FormInputState.new('').map(validateNotEmpty('Please enter a Note.'));
        this.selectedReason = FormInputState.new('').map(validateNotEmpty('Please select a Reason.'));
        this.selectedRiskLevel = FormInputState.new('').map(validateNotEmpty('Please select a Risk level.'));
        this.interactionStatus = FormInputState.new('').map(validateNotEmpty('Please select a Status.'));
        this.profitLoss = FormInputState.new('').map((newAmount) => {
            if (newAmount === '') {
                return Result.createOk(undefined);
            }

            try {
                const convertedNewAmount = new Amount(newAmount);

                return Result.createOk(convertedNewAmount);
            } catch (error) {
                return Result.createError('Incorrect number');
            }
        });

        this.form = FormModel.group({
            accountId: FormInputState.new(this.customerId().toString()),
            universe: FormInputState.new(this.configState.universe),
            interactionType: this.selectedInteractionType,
            interactionTimestamp: FormInputState.new(DateTime.current().format('YYYY-MM-DDTHH:mm:ssZ').toString()),
            nextReviewTimestamp: this.nextReviewTimestamp,
            note: this.note,
            reason: this.selectedReason,
            riskLevel: this.selectedRiskLevel,
            interactionStatus: this.interactionStatus,
            profitLoss: this.profitLoss,
            agent: FormInputState.new(this.common.session.userId?.toString()).map(
                validateNotEmpty('Backoffice user is not logged in.')
            ),
        });

        makeAutoObservable(this);
    }

    @computed public get interactionTypeOptions(): typeof INTERACTIONS_TYPE {
        return INTERACTIONS_TYPE;
    }

    @computed public get reasonOptions(): typeof REASON_TYPE {
        return REASON_TYPE;
    }

    @computed public get riskLevelOptions(): typeof MOH_RISK_LEVEL {
        return MOH_RISK_LEVEL;
    }

    @computed public get statusOptions(): typeof INTERACTION_STATUS {
        return INTERACTION_STATUS;
    }

    private mapLabel = (
        arrayValue: {
            value: string;
            label: string;
        }[],
        itemValue: string
    ): string => {
        return (
            arrayValue.find((item) => {
                return item.value === itemValue;
            })?.label ?? itemValue
        );
    };

    @computed private get getInteractionsList(): InteractionsTypeReadOnlyType {
        const interactionsList = this.getInteractionsResource.get();

        if (interactionsList.type === 'ready') {
            return interactionsList.value.map((item) => {
                return {
                    ...item,
                    agent: this.getStaffMap.get(item.agent) ?? item.agent,
                    profitLoss:
                        item.profitLoss === undefined || item.profitLoss === ''
                            ? undefined
                            : new Amount(item.profitLoss),
                    labels: {
                        interactionType: this.mapLabel(this.interactionTypeOptions, item.interactionType),
                        reason: this.mapLabel(this.reasonOptions, item.reason),
                        riskLevel: this.mapLabel(this.riskLevelOptions, item.riskLevel),
                        interactionStatus: this.mapLabel(this.statusOptions, item.interactionStatus),
                    },
                };
            });
        }

        return [];
    }

    @computed public get sortedInteractions(): InteractionsTypeReadOnlyType {
        if (this.sortBy === 'latest') {
            return this.getInteractionsList.sort((a, b) => {
                return (
                    (DateTime.from(b.interactionTimestamp)?.unixMs() ?? 0) -
                    (DateTime.from(a.interactionTimestamp)?.unixMs() ?? 0)
                );
            });
        } else if (this.sortBy === 'latestMohId') {
            return this.getInteractionsList.sort((a, b) => {
                return b.mohId - a.mohId;
            });
        }
        return this.getInteractionsList;
    }
    @computed private get getStaffMap(): Map<string, string> {
        const staffList = this.getStaffListResource.get();

        if (staffList.type === 'ready') {
            return new Map<string, string>(
                staffList.value.map((item) => [
                    item.id.toString(),
                    `${item.properties.firstName} ${item.properties.surname}`,
                ])
            );
        }
        return new Map();
    }

    @action public onChangeDate = async (startTime: string | null): Promise<void> => {
        this.nextReviewTimestamp.setValue(startTime ?? '');
    };

    private getFormValue = (): ApiCreateMarkersOfHarmParamsType['requestBody'] | null => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            const validInteractionType = isValidType(
                MarkersOfHarmMohInteractionTypeZod,
                result.value.data.interactionType
            );
            const validRiskLevel = isValidType(MarkersOfHarmMohRiskLevelZod, result.value.data.riskLevel);
            const validInteractionStatus = isValidType(
                MarkersOfHarmMohInteractionStatusZod,
                result.value.data.interactionStatus
            );
            if (validInteractionType === false || validRiskLevel === false || validInteractionStatus === false) {
                console.error('err validInteractionType');
                return null;
            }
            const validNextReviewTimestamp =
                result.value.data.nextReviewTimestamp === ''
                    ? undefined
                    : DateTime.from(result.value.data.nextReviewTimestamp)?.format('YYYY-MM-DDTHH:mm:ssZ');

            return {
                ...result.value.data,
                interactionType: validInteractionType,
                riskLevel: validRiskLevel,
                interactionStatus: validInteractionStatus,
                nextReviewTimestamp: validNextReviewTimestamp,
                profitLoss: result.value.data.profitLoss?.value ?? '',
            };
        } else if (Array.isArray(result.value.message)) {
            addError(`Cannot submit form`);
        }
        return null;
    };

    @action public onAddInteraction = async (): Promise<void> => {
        const resultForm = this.getFormValue();
        if (resultForm === null) {
            console.log('error submit!');
            return;
        }

        try {
            await this.apiMarkersOfHarm.createMarkersOfHarm({ requestBody: resultForm });
            this.resetForm();
            await this.getInteractionsResource.refresh();
        } catch (err) {
            addError('There was a problem with request');
            console.log(err);
        }
    };

    public onEditInteraction = (mohId: number): void => {
        const editedItem = this.getInteractionsList.find((item) => {
            return item.mohId === mohId;
        });

        if (editedItem === undefined) {
            addError('Failed to edit');
            return;
        }
        this.editingIndex = mohId;
        this.selectedInteractionType.setValue(editedItem.interactionType);
        this.nextReviewTimestamp.setValue(
            editedItem.nextReviewTimestamp === undefined
                ? ''
                : DateTime.current().format('YYYY-MM-DDTHH:mm:ssZ').toString()
        );

        this.note.setValue(editedItem.note ?? '');
        this.selectedReason.setValue(editedItem.reason);
        this.selectedRiskLevel.setValue(editedItem.riskLevel);
        this.interactionStatus.setValue(editedItem.interactionStatus);
        this.profitLoss.setValue(editedItem.profitLoss?.value ?? '');
    };

    @action public onUpdateInteraction = async (mohId: number): Promise<void> => {
        const resultForm = this.getFormValue();
        if (resultForm === null) {
            console.log('error update!');
            return;
        }

        try {
            await this.apiMarkersOfHarm.patchMarkersOfHarm({ moh_id: mohId, requestBody: resultForm });
            this.resetForm();
            this.editingIndex = -1;
            await this.getInteractionsResource.refresh();
        } catch (err) {
            addError('There was a problem updating the interaction');
            console.log(err);
        }
    };

    public onCancelEdit = (): void => {
        this.resetForm();
        this.editingIndex = -1;
    };

    @action public onRemoveInteraction = async (mohId: number): Promise<void> => {
        const confirmed = await confirmation('Are you sure you want to remove this interaction?', 'Yes');

        //@ts-expect-error
        if (confirmed === 'Yes') {
            try {
                await this.apiMarkersOfHarm.deleteMarkersOfHarm({ id: mohId });
                await this.getInteractionsResource.refresh();
            } catch (err) {
                addError('There was a problem removing the interaction');
                console.log(err);
            }
        }
    };

    private resetForm = (): void => {
        this.selectedInteractionType.reset();
        this.nextReviewTimestamp.reset();
        this.note.reset();
        this.selectedReason.reset();
        this.selectedRiskLevel.reset();
        this.interactionStatus.reset();
        this.profitLoss.reset();
    };
}
